import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useCacheQuery } from 'gossamer-front-end';
import { BookingExtendedView, BookingExtendedViewRow } from 'cb-model';
import { TaskRow } from 'gossamer-model';
import { padNumber, truncate } from 'gossamer-unified';
import classes from './Header.module.css';
import { formatEventUrl } from '../event/helpers';

interface NowEventBannerProps {
  userId: string | null;
}

export const NowEventBanner = (props: NowEventBannerProps) => {
  const [now, setNow] = useState<Date>(new Date());
  const inOneHour = new Date(now);
  inOneHour.setHours(inOneHour.getHours() + 1);

  // Fetch bookings for events happening now or within the next hour
  const upcomingBookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          { fieldId: 'user_id', operator: 'eq', value: props.userId },
          { fieldId: 'start_at', operator: 'gt', value: now.toISOString() },
          { fieldId: 'start_at', operator: 'lt', value: inOneHour.toISOString() },
          { fieldId: 'type', operator: 'ne', value: 'on-demand' }, // exclude on-demand events
          { fieldId: 'booking_status', operator: 'eq', value: 'active' }, // only active bookings
        ],
      },
      order: [['start_at', 'ASC']],
      limit: 1,
    },
    !props.userId, // skip if no user
  );

  const nowBookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          { fieldId: 'user_id', operator: 'eq', value: props.userId },
          { fieldId: 'start_at', operator: 'lt', value: now.toISOString() },
          { fieldId: 'end_at', operator: 'gt', value: now.toISOString() },
          { fieldId: 'type', operator: 'ne', value: 'on-demand' }, // exclude on-demand events
          { fieldId: 'booking_status', operator: 'eq', value: 'active' }, // only active bookings
        ],
      },
      order: [['start_at', 'ASC']],
      limit: 1,
    },
    !props.userId, // skip if no user
  );

  useEffect(() => {
    const interval = setInterval(
      () => {
        setNow(new Date());
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, []);

  if (!props.userId) return null;

  const upcomingBookings = upcomingBookingsQuery.data || [];
  const nowBookings = nowBookingsQuery.data || [];

  if (upcomingBookings.length === 0 && nowBookings.length === 0) {
    return null;
  }

  return (
    <div className={classes.banner}>
      {nowBookings.length > 0 && (
        <div>
          Happening now: <EventLink row={nowBookings[0]} />
        </div>
      )}
      {upcomingBookings.length > 0 && (
        <div>
          Up next: <EventLink row={upcomingBookings[0]} />
        </div>
      )}
    </div>
  );
};

const EventLink = (props: { row: BookingExtendedViewRow }) => {
  const start = new Date(props.row.start_at);
  const text = [
    truncate(props.row.title || '', 80),
    'at ' + padNumber(start.getHours(), 2) + ':' + padNumber(start.getMinutes(), 2),
    truncate(props.row.location || '', 80),
  ]
    .filter((str) => !!str)
    .join(', ');
  return (
    <NavLink to={formatEventUrl(props.row.title || '', props.row.event_id)} className={classes.bannerLink}>
      {text}
    </NavLink>
  );
};

interface ActiveTaskBannerProps {
  nextActiveTask: TaskRow;
}

export const ActiveTaskBanner = ({ nextActiveTask }: ActiveTaskBannerProps) => {
  if (!nextActiveTask) return null;
  return (
    <div className={classes.banner}>
      <div>
        Next Active Task:{' '}
        <NavLink to={nextActiveTask.url} className={classes.taskLabel}>
          {nextActiveTask.label}
        </NavLink>{' '}
        <span className={classes.taskDot}>•</span>
      </div>
    </div>
  );
};
